import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { CallDirectionTypes } from 'generated/graphql-types';
import { EMPTY, of } from 'rxjs';
import { catchError, debounceTime, map, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { AppState } from 'src/app/app.reducer';
import { selectCurrentRoute } from 'src/app/app.selectors';
import { getCurrentUserIsSystemUser } from 'src/app/authentication/state/authentication.selector';
import { CallDetailDialogComponent } from 'src/app/common-components/call-details-dialog/call-detail-dialog.component';
import { LcsDialogService } from 'src/app/common-components/dialog/dialog.service';
import { getCallExtension } from 'src/app/common-components/helpers/call-logs-helper';
import { deleteCallRecordingSuccess } from 'src/app/pages/call-logs-register/state/call-logs-register.actions';
import { getHangUpCallsPrivileges } from 'src/app/privileges/privileges.selector';
import {
   ActiveCallsRegisterListItem,
   ActiveCallsRegisterResults,
} from '../../models/active-calls-register-results.model';
import { GetCallDetails, GetCallDetails_callDetailsConnection_items } from '../../queries/generated/GetCallDetails';
import { GetCallDetailsReadOnly } from '../../queries/generated/GetCallDetailsReadOnly';
import { ActiveCallsRegisterApiService } from '../../services/active-calls-register-api.service';
import {
   ActiveCallRegisterType,
   ActiveCallsRegisterSessionStorageService,
} from '../../services/active-calls-register-session-storage.service';
import * as AccountCallHistoryActions from './account-call-history.actions';
import { accountCallHistoryRegisterAdapterActions } from './account-call-history.actions';
import { selectEndCursor, selectOrderingOptions, selectQuickFilters } from './account-call-history.reducer';
import { selectFilters, selectSortedActiveCalls } from './account-call-history.selectors';

@Injectable()
export class AccountCallHistoryRegisterEffects {
   constructor(
      private actions$: Actions,
      private store: Store<AppState>,
      private activeCallsRegisterApiService: ActiveCallsRegisterApiService,
      private dialog: MatDialog,
      private dialogService: LcsDialogService,
      private sessionStorageService: ActiveCallsRegisterSessionStorageService
   ) {}

   loadActiveCallsList$ = createEffect(() =>
      this.actions$.pipe(
         ofType(accountCallHistoryRegisterAdapterActions.pageEnter, deleteCallRecordingSuccess),
         withLatestFrom(
            this.store.pipe(select(selectFilters)),
            this.store.pipe(select(selectCurrentRoute)),
            this.store.pipe(select(selectOrderingOptions)),
            this.store.pipe(select(selectQuickFilters))
         ),
         switchMap(([_, filters, route, orderingOptions, quickFilters]) => {
            const currentRouteUrl = route.url.map((segment) => segment.path).join('/');
            const sessionOrderingOptions = this.sessionStorageService.getOrderingOptions(
               ActiveCallRegisterType.AccountHistory
            );

            const ordering = sessionOrderingOptions ?? orderingOptions;
            if (sessionOrderingOptions) {
               this.store.dispatch(
                  AccountCallHistoryActions.setOrderingOptionsFromSessionStorage({ orderingOptions: ordering })
               );
            }

            return this.activeCallsRegisterApiService
               .getActiveCalls(null, filters, quickFilters, ordering, currentRouteUrl)
               .pipe(
                  map((result) =>
                     accountCallHistoryRegisterAdapterActions.loadSuccess(
                        this.convertGetActiveCallsResultToLoadSuccessProps(
                           result.data as GetCallDetailsReadOnly,
                           currentRouteUrl
                        )
                     )
                  ),
                  catchError((error) => of(accountCallHistoryRegisterAdapterActions.loadFailure(error)))
               );
         })
      )
   );

   loadActiveCallsPage$ = createEffect(() =>
      this.actions$.pipe(
         ofType(accountCallHistoryRegisterAdapterActions.loadPage),
         withLatestFrom(
            this.store.pipe(select(selectEndCursor)),
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectFilters)),
            this.store.pipe(select(selectOrderingOptions)),
            this.store.pipe(select(selectCurrentRoute))
         ),
         switchMap(([_, endCursor, quickFilters, filters, orderingOptions, route]) => {
            const currentRouteUrl = route.url.map((segment) => segment.path).join('/');
            return this.activeCallsRegisterApiService
               .getActiveCalls(endCursor, filters, quickFilters, orderingOptions, currentRouteUrl)
               .pipe(
                  map((result) => {
                     const sessionOrderingOptions = this.sessionStorageService.getOrderingOptions(
                        ActiveCallRegisterType.AccountHistory
                     );
                     if (sessionOrderingOptions) {
                        orderingOptions = sessionOrderingOptions;
                        this.store.dispatch(
                           AccountCallHistoryActions.setOrderingOptionsFromSessionStorage({ orderingOptions })
                        );
                     }
                     return accountCallHistoryRegisterAdapterActions.loadPageSuccess(
                        this.convertGetActiveCallsResultToLoadSuccessProps(
                           result.data as GetCallDetailsReadOnly,
                           currentRouteUrl
                        )
                     );
                  }),
                  catchError((error) => of(accountCallHistoryRegisterAdapterActions.loadPageFailure(error)))
               );
         })
      )
   );

   orderingOptionsChanged$ = createEffect(() =>
      this.actions$.pipe(
         ofType(accountCallHistoryRegisterAdapterActions.orderingOptionsChanged),
         withLatestFrom(
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectOrderingOptions)),
            this.store.pipe(select(selectFilters)),
            this.store.pipe(select(selectCurrentRoute))
         ),
         switchMap(([_, quickfilters, orderingOptions, filters, route]) => {
            this.sessionStorageService.setOrderingOptions(ActiveCallRegisterType.AccountHistory, orderingOptions);
            const currentRouteUrl = route.url.map((segment) => segment.path).join('/');
            return this.loadNewData(quickfilters, filters, orderingOptions, currentRouteUrl);
         })
      )
   );

   toggleAutoRefresh$ = createEffect(() =>
      this.actions$.pipe(
         ofType(AccountCallHistoryActions.toggleAutoRefresh),
         debounceTime(300),
         withLatestFrom(
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectFilters)),
            this.store.pipe(select(selectOrderingOptions)),
            this.store.pipe(select(selectCurrentRoute))
         ),
         switchMap(([action, quickFilters, filters, orderingOptions, route]) => {
            if (!action.isAutoRefresh) return EMPTY;
            const currentRouteUrl = route.url.map((segment) => segment.path).join('/');
            return this.loadNewData(quickFilters, filters, orderingOptions, currentRouteUrl);
         })
      )
   );

   quickFilterChanged$ = createEffect(() =>
      this.actions$.pipe(
         ofType(accountCallHistoryRegisterAdapterActions.quickFilterChanged),
         withLatestFrom(
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectFilters)),
            this.store.pipe(select(selectOrderingOptions)),
            this.store.pipe(select(selectCurrentRoute))
         ),
         switchMap(([_, quickFilters, filters, orderingOptions, route]) => {
            const currentRouteUrl = route.url.map((segment) => segment.path).join('/');
            return this.loadNewData(quickFilters, filters, orderingOptions, currentRouteUrl);
         })
      )
   );

   openDialog$ = createEffect(
      () =>
         this.actions$.pipe(
            ofType(AccountCallHistoryActions.openDialog),
            withLatestFrom(
               this.store.pipe(select(getCurrentUserIsSystemUser)),
               this.store.pipe(select(getHangUpCallsPrivileges))
            ),
            switchMap(([action, isSystemUser, canHangupCalls]) =>
               this.activeCallsRegisterApiService.getCallDetailDialogData(action.callLogs.callDetailId).pipe(
                  switchMap((callData) => {
                     const ref = this.dialog.open(CallDetailDialogComponent, {
                        data: {
                           callLogs: action.callLogs,
                           callData: callData?.data?.callDetail,
                           canHangupCalls: canHangupCalls,
                           relatedCallDetails: (callData as any).relatedCallDetails,
                           isSystemUser,
                           registerType: ActiveCallRegisterType.AccountHistory,
                        },
                        width: '800px',
                        autoFocus: false,
                     });
                     return ref.afterClosed().pipe(
                        tap((result) => {
                           if (result?.navigateTo) {
                              const callLogs$ = this.store.pipe(
                                 select(selectSortedActiveCalls),
                                 take(1),
                                 map((rows) => rows.find((row) => row.callDetailId == result.navigateTo))
                              );
                              callLogs$.subscribe((callLogs) => {
                                 if (callLogs) {
                                    this.store.dispatch(
                                       AccountCallHistoryActions.openDialog({
                                          callLogs: callLogs,
                                       })
                                    );
                                 }
                              });
                           }
                        })
                     );
                  })
               )
            )
         ),
      { dispatch: false }
   );

   private loadNewData(quickFilters: any, filters: any, orderingOptions: any, currentRouteUrl: string) {
      return this.activeCallsRegisterApiService
         .getActiveCalls(null, filters, quickFilters, orderingOptions, currentRouteUrl)
         .pipe(
            map((result) =>
               accountCallHistoryRegisterAdapterActions.loadSuccess(
                  this.convertGetActiveCallsResultToLoadSuccessProps(
                     result.data as GetCallDetailsReadOnly,
                     currentRouteUrl
                  )
               )
            ),
            catchError((error) => of(accountCallHistoryRegisterAdapterActions.loadFailure(error)))
         );
   }

   private convertGetActiveCallsResultToLoadSuccessProps(
      getCallDetails: GetCallDetails | GetCallDetailsReadOnly,
      currentRouteUrl: string
   ): ActiveCallsRegisterResults {
      const isReadOnly = 'callDetailReadOnliesConnection' in getCallDetails;
      const connection = isReadOnly
         ? (getCallDetails as GetCallDetailsReadOnly).callDetailReadOnliesConnection
         : (getCallDetails as GetCallDetails).callDetailsConnection;

      return {
         endCursor: connection.pageInfo.endCursor,
         hasNextPage: connection.pageInfo.hasNextPage,
         items: connection.items.map(this.convertActiveCallsRegisterListItem),
         totalCount: connection.totalCount,
      };
   }

   private convertActiveCallsRegisterListItem(
      item: GetCallDetails_callDetailsConnection_items
   ): ActiveCallsRegisterListItem {
      const isPhoneCallThatWentDirectlyToSourceExtension =
         item.sourceExtension?.extensionId === item.destinationExtension?.extensionId &&
         item.callDirectionTypeId === CallDirectionTypes.Internal &&
         item.destinationNumber == item.sourceExtension?.number &&
         item.destinationNumber == item.destinationExtension?.number;
      return {
         accountName: item.account?.name ?? null,
         callDestination: getCallExtension(
            { ...item.destinationExtension },
            item.callDirectionTypeId,
            item.destinationNumber,
            true
         ),
         callDetailId: item.callDetailId,
         callDirection: item.callDirectionTypeId,
         callDispositionTypeId: item.callDispositionTypeId,
         callRecordingId: item.callRecording?.callRecordingId ?? null,
         callSource: getCallExtension({ ...item.sourceExtension }, item.callDirectionTypeId, item.sourceNumber),
         destinationExtension: item.destinationExtension,
         destinationNumber: item.destinationNumber,
         originalDestinationNumber: item.originalDestinationNumber,
         phoneNumber: item.phoneNumber?.phoneNumber ?? null,
         callRate: item.callRate ?? 0,
         startTime: item.startTime,
         endTime: item?.endTime ?? null,
         callerChannelId: item.callerChannelId,
         serverName: item.serverName,
         sipCallId: item.sipCallId,
         providerId: item.providerId,
         sourceProviderId: item.sourceProviderId,
         destinationProviderId: item.destinationProviderId,
         callRecordingFile: item.callRecording?.file?.fileId,
         isPhoneCallThatWentDirectlyToSourceExtension: isPhoneCallThatWentDirectlyToSourceExtension,
      };
   }
}
